import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { ShieldCheck, Lock, SearchCheck, Megaphone, Handshake, ArrowRight, Percent, Info, BadgeCheck, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { t } from "../../lib/i18n";

const PHASES = [
  {
    icon: SearchCheck,
    kicker: t("Fase 01"),
    title: t("Análisis y Preparación"),
    lead: t("Conocemos tu empresa a fondo y la preparamos para que luzca su mejor versión ante los compradores."),
    items: [
      "Entendemos tu historia, tu equipo y lo que hace única a tu empresa",
      "Ponemos tus cifras en orden y destacamos el valor real de tu negocio",
      "Preparamos por ti toda la documentación que un comprador serio espera ver",
    ],
  },
  {
    icon: Megaphone,
    kicker: t("Fase 02"),
    title: t("Salida al Mercado"),
    lead: t("Buscamos y filtramos a los compradores adecuados para que solo hables con quien de verdad merece la pena."),
    items: [
      "Solo llegan a ti compradores cualificados y aprobados previamente",
      "Organizamos las reuniones para que tú solo tengas que presentarte",
      "Gestionamos la información y las ofertas sin que pierdas el control",
    ],
  },
  {
    icon: Handshake,
    kicker: t("Fase 03"),
    title: t("Cierre"),
    lead: t("Te acompañamos en la negociación hasta la firma para que vendas bien, con seguridad y sin sorpresas."),
    items: [
      "Negociamos contigo las mejores condiciones y la estructura del acuerdo",
      "Estamos a tu lado en la LOI, la Due Diligence y el contrato final",
      "Coordinamos el cierre para que todo llegue a buen puerto",
    ],
  },
];

const FEE_POINTS = [
  {
    icon: BadgeCheck,
    figure: "0 €",
    title: t("Registro y proceso gratuitos"),
    text: t("Darte de alta, preparar tu empresa y salir al mercado no te cuesta nada. Solo pagas si la operación se cierra."),
  },
  {
    icon: Percent,
    figure: t("3 % + IVA"),
    title: t("Honorarios solo a éxito"),
    text: t("Sobre el valor de la operación, únicamente si completas la venta con un comprador de la plataforma. Sin cierre, sin coste."),
    dark: true,
  },
  {
    icon: FileSignature,
    figure: t("1 clic"),
    title: t("Acuerdo cuando haya interés"),
    text: t("En cuanto un comprador muestre interés real, te enviaremos el acuerdo de asesoramiento para formalizar la relación. Sin letra pequeña."),
  },
];

const EBITDA_OPTIONS = [
  { value: "lt_800k", label: t("Menos de 800.000 €") },
  { value: "800k_2m", label: t("De 800.000 € a 2 M€") },
  { value: "2m_5m", label: t("De 2 a 5 M€") },
  { value: "gt_5m", label: t("Más de 5 M€") },
];

export default function ConfidentialityNDA() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ebitda, setEbitda] = useState("");
  const tooSmall = ebitda === "lt_800k";
  const canContinue = ebitda && !tooSmall;

  const start = async () => {
    if (!canContinue) return;
    setLoading(true);
    try {
      await api.post("/users/accept-nda", { ebitda_range: ebitda });
      await refresh();
      navigate("/seller/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ha ocurrido un error. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        <div className="max-w-3xl mb-10 fade-up">
          <span className="font-mono-label text-gold">{t("Bienvenido a Relevo · Vendedores")}</span>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3" data-testid="seller-welcome-title">
            {t("Tu proceso de venta, respaldado por expertos y tecnología")}
          </h1>
        </div>

        <div className="hairline bg-[#1E3A5F] text-bone p-7 md:p-8 flex items-start gap-5 fade-up-delay-1" data-testid="confidentiality-block">
          <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-white/10">
            <ShieldCheck className="w-6 h-6 text-[#E4B54B]" />
          </div>
          <div>
            <span className="font-mono-label text-gold">{t("Confidencialidad garantizada")}</span>
            <p className="text-base md:text-lg leading-relaxed mt-1.5">
              {t("Toda la información que compartas en RELEVO está protegida bajo estricta confidencialidad. Solo los compradores que hayan sido previamente aprobados por ti y que hayan firmado un NDA tendrán acceso a los datos de tu empresa. Tú decides quién ve qué.")}
            </p>
          </div>
        </div>

        <div className="mt-12 fade-up-delay-2">
          <span className="font-mono-label text-[#E0712C]">{t("Te acompañamos en cada paso")}</span>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-2 max-w-2xl" data-testid="services-heading">
            {t("Estamos contigo en todo el proceso")}
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            {t("Nuestro equipo especializado y las herramientas de RELEVO trabajan por ti en cada fase, para que vendas bien y sin complicaciones.")}
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-0 hairline border-r-0 border-b-0" data-testid="service-phases">
            {PHASES.map((p, i) => (
              <div key={i} className="hairline border-l-0 border-t-0 p-7 bg-card flex flex-col" data-testid={`service-phase-${i + 1}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 hairline flex items-center justify-center bg-bone">
                    <p.icon className="w-5 h-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <div className="font-mono-label text-[#E0712C]">{p.kicker}</div>
                    <h3 className="font-serif-display text-xl leading-tight">{p.title}</h3>
                  </div>
                </div>
                <p className="text-sm md:text-base leading-relaxed mb-5">{p.lead}</p>
                <ul className="space-y-3 mt-auto">
                  {p.items.map((it, j) => (
                    <li key={j} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 flex-shrink-0 bg-[#E0712C]" />
                      <span>{t(it)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 fade-up-delay-3" data-testid="fee-model-block">
          <span className="font-mono-label text-[#E0712C]">{t("Transparencia total")}</span>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-2 max-w-2xl" data-testid="fee-heading">
            {t("¿Cuánto te va a costar?")}
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            {t("Nada por adelantado. Solo cobramos cuando tú cobras.")}
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-0 hairline border-r-0 border-b-0">
            {FEE_POINTS.map((f, i) => (
              <div
                key={i}
                className={`hairline border-l-0 border-t-0 p-7 flex flex-col ${f.dark ? "bg-[#1E3A5F] text-bone" : "bg-card"}`}
                data-testid={`fee-point-${i + 1}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className={`w-11 h-11 hairline flex items-center justify-center ${f.dark ? "bg-white/10 border-white/20" : "bg-bone"}`}>
                    <f.icon className={`w-5 h-5 ${f.dark ? "text-[#E4B54B]" : "text-[#1E3A5F]"}`} />
                  </div>
                  <span className={`font-mono-label ${f.dark ? "text-gold" : "text-[#E0712C]"}`}>{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className={`font-serif-display leading-none ${f.dark ? "text-4xl md:text-5xl text-bone" : "text-3xl md:text-4xl text-[#1E3A5F]"}`}>
                  {f.figure}
                </div>
                <h3 className={`font-semibold mt-3 ${f.dark ? "text-bone" : ""}`}>{f.title}</h3>
                <p className={`text-sm leading-relaxed mt-2 ${f.dark ? "text-bone/80" : "text-muted-foreground"}`}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 hairline bg-card p-7 md:p-8 fade-up-delay-3" data-testid="prequal-block">
          <span className="font-mono-label text-[#E0712C]">{t("Precualificación")}</span>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-2" data-testid="prequal-question">
            {t("¿Cuál es el EBITDA anual de tu empresa?")}
          </h2>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-0 hairline border-r-0 border-b-0" role="radiogroup" data-testid="prequal-options">
            {EBITDA_OPTIONS.map((o) => {
              const selected = ebitda === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setEbitda(o.value)}
                  className={`hairline border-l-0 border-t-0 px-4 py-4 text-left text-sm font-medium transition-colors ${
                    selected ? "bg-[#1E3A5F] text-bone" : "bg-card hover:bg-bone"
                  }`}
                  data-testid={`prequal-option-${o.value}`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          {tooSmall && (
            <div className="mt-5 hairline border-[#E0712C]/50 bg-[#E0712C]/10 p-5 flex items-start gap-3" data-testid="prequal-too-small-message">
              <Info className="w-5 h-5 text-[#C25F1E] flex-shrink-0 mt-0.5" />
              <p className="text-sm md:text-base leading-relaxed">
                {t("Este tipo de operaciones requieren un tamaño mínimo de empresa para ser viables en un proceso de M&A. Te esperamos cuando alcances ese punto, ¡mucho ánimo!")}
              </p>
            </div>
          )}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 fade-up-delay-3">
          <span className="font-mono-label text-muted-foreground flex items-center gap-2">
            <Lock className="w-3 h-3" />{" "}{t("Almacenamiento cifrado · Datos en la UE")}
          </span>
          <Button
            onClick={start}
            disabled={loading || !canContinue}
            className="rounded-none bg-[#C25F1E] hover:bg-[#A94F15] text-white font-semibold h-12 px-8"
            data-testid="nda-accept-btn"
          >
            {loading ? t("Un momento…") : t("Comenzar mi proceso de venta")} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </Layout>
  );
}
